import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { CheckCircleIcon, DocumentTextIcon, TruckIcon } from '@heroicons/react/24/outline'
import { useCartStore } from '../stores/cartStore'

const OrderConfirmationPage: React.FC = () => {
  const location = useLocation()
  const { clearCart } = useCartStore()
  
  // Get order data from navigation state (passed from checkout)
  const orderData = location.state?.orderData

  useEffect(() => {
    // Clear cart after successful order
    clearCart()
  }, [clearCart])

  if (!orderData) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-lg">
            <h1 className="text-2xl font-bold text-gray-800 mb-4">Bestellung nicht gefunden</h1>
            <p className="text-gray-600 mb-6">
              Es scheint, als wäre ein Fehler aufgetreten. Bitte versuchen Sie es erneut.
            </p>
            <Link 
              to="/repair" 
              className="bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Neue Reparatur anfragen
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto">
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircleIcon className="w-12 h-12 text-green-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Bestellung erfolgreich aufgegeben!
            </h1>
            <p className="text-xl text-gray-600">
              Vielen Dank für Ihr Vertrauen in PhoneMedic
            </p>
          </div>

          {/* Order Summary */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Bestellübersicht</h2>
            
            <div className="border-b pb-4 mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Bestellnummer:</span>
                <span className="text-indigo-600 font-bold">{orderData.orderNumber || 'ORD-' + Date.now()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="font-medium">Gesamtbetrag:</span>
                <span className="text-xl font-bold text-indigo-600">{orderData.total?.toFixed(2) || '0.00'}€</span>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">Bestellte Reparaturen:</h3>
              {orderData.items?.map((item: any, index: number) => (
                <div key={index} className="bg-gray-50 p-3 rounded-lg">
                  <div className="font-medium">{item.title}</div>
                  <div className="text-sm text-gray-600">
                    {item.device?.brand} {item.device?.deviceModel}
                    {item.isExpress && <span className="text-orange-600 ml-2">Express-Service</span>}
                  </div>
                  <div className="text-right text-indigo-600 font-semibold">{item.price?.toFixed(2)}€</div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Steps */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4">Nächste Schritte</h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <DocumentTextIcon className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold">1. Versandlabel erhalten</h3>
                  <p className="text-gray-600 text-sm">
                    Sie erhalten in wenigen Minuten eine E-Mail mit dem kostenlosen DHL-Versandlabel.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <TruckIcon className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold">2. Gerät sicher verpacken und einsenden</h3>
                  <p className="text-gray-600 text-sm">
                    Verpacken Sie Ihr Gerät sicher und senden es mit dem Versandlabel an uns.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold">3. Reparatur & Rücksendung</h3>
                  <p className="text-gray-600 text-sm">
                    Wir reparieren Ihr Gerät professionell und senden es versichert zurück.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-slate-800 text-white rounded-xl p-6 text-center">
            <h2 className="text-xl font-semibold mb-4">Fragen zu Ihrer Bestellung?</h2>
            <p className="mb-4">
              Unser Support-Team hilft Ihnen gerne weiter:
            </p>
            <div className="space-y-2">
              <div>📞 <a href="tel:01605010907" className="hover:text-indigo-300">0160-5010907</a></div>
              <div>📧 service@phone-medic.de</div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="text-center mt-8 space-x-4">
            <Link 
              to="/repair" 
              className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
            >
              Weitere Reparatur anfragen
            </Link>
            <Link 
              to="/" 
              className="inline-block bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
            >
              Zur Startseite
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrderConfirmationPage